import { useEffect, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  ErrorState,
  FilterToolbar,
  SearchField,
  SegmentedControl,
  SelectField,
  Skeleton,
} from '@shared/design-system/ui'
import { RotateCw } from 'lucide-react'
import { toast } from '@shared/lib/toast/toast-store'
import { useDebounce } from '@shared/lib/use-debounce'
import {
  recuperaAiCartsInfiniteQuery,
  recuperaAiMetricsQuery,
  recuperaAiProductsQuery,
  recuperaAiStatusQuery,
} from '../api/recupera-ai.queries'
import { useRecuperaOptInMutation } from '../api/recupera-ai.mutations'
import type { RecuperaCartSort, RecuperaCartStatus } from '../api/recupera-ai.contract'
import { RecuperaMetricsCards } from './RecuperaMetricsCards'
import { RecuperaCartsTable } from './RecuperaCartsTable'
import { RecuperaOptIn } from './RecuperaOptIn'
import {
  filtersGroup,
  header,
  headerText,
  page,
  pushRight,
  searchGrow,
  subtitle,
  title,
} from './recupera-ai.css'

export type RecuperaPeriod = '24h' | '7d' | '30d' | 'month' | 'all'

export type RecuperaAiSearch = {
  status?: RecuperaCartStatus
  sort?: RecuperaCartSort
  q?: string
  product?: string
  period?: RecuperaPeriod
}

interface RecuperaAiScreenProps {
  search: RecuperaAiSearch
  onSearchChange: (next: RecuperaAiSearch) => void
}

const PERIOD_OPTIONS: ReadonlyArray<{ value: RecuperaPeriod; label: string }> = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: 'month', label: 'Mês' },
  { value: 'all', label: 'Tudo' },
]

const STATUS_OPTIONS: ReadonlyArray<{ value: RecuperaCartStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'abandoned', label: 'Em andamento' },
  { value: 'recovered', label: 'Recuperado' },
  { value: 'external', label: 'Convertido (outra origem)' },
]

const SORT_OPTIONS: ReadonlyArray<{ value: RecuperaCartSort; label: string }> = [
  { value: 'date_desc', label: 'Data (mais recente)' },
  { value: 'date_asc', label: 'Data (mais antiga)' },
  { value: 'name_asc', label: 'Produto (A-Z)' },
  { value: 'price_desc', label: 'Valor (maior)' },
  { value: 'price_asc', label: 'Valor (menor)' },
]

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function rangeFor(period: RecuperaPeriod): { from: string; to: string } {
  const today = new Date()
  const to = isoDate(today)
  if (period === 'all') return { from: '2020-01-01', to }
  const from = new Date(today)
  if (period === '24h') from.setUTCDate(from.getUTCDate() - 1)
  else if (period === '7d') from.setUTCDate(from.getUTCDate() - 6)
  else if (period === 'month') from.setUTCDate(1)
  else from.setUTCDate(from.getUTCDate() - 29)
  return { from: isoDate(from), to }
}

export function RecuperaAiScreen({ search, onSearchChange }: RecuperaAiScreenProps) {
  const period = search.period ?? '30d'
  const statusFilter = search.status
  const sort = search.sort ?? 'date_desc'
  const range = rangeFor(period)

  const [searchText, setSearchText] = useState(search.q ?? '')
  const debouncedSearch = useDebounce(searchText, 500)

  useEffect(() => {
    const trimmed = debouncedSearch.trim()
    const next = trimmed === '' ? undefined : trimmed
    if (next === search.q) return
    onSearchChange({ ...search, q: next })
  }, [debouncedSearch, search, onSearchChange])

  const status = useQuery(recuperaAiStatusQuery())
  const active = status.data?.is_active === true
  const optIn = useRecuperaOptInMutation()

  const metrics = useQuery({ ...recuperaAiMetricsQuery(range), enabled: active })
  const products = useQuery({ ...recuperaAiProductsQuery(), enabled: active })
  const carts = useInfiniteQuery({
    ...recuperaAiCartsInfiniteQuery({
      ...(statusFilter !== undefined ? { status: statusFilter } : {}),
      sort,
      ...(search.q !== undefined ? { search: search.q } : {}),
      ...(search.product !== undefined ? { product: search.product } : {}),
      dateFrom: range.from,
      dateTo: range.to,
    }),
    enabled: active,
  })

  const productOptions = [
    { value: '', label: 'Todos os produtos' },
    ...(products.data?.items ?? []).map((name) => ({ value: name, label: name })),
  ]

  const activate = () => {
    optIn.mutate(undefined, {
      onSuccess: () => toast.success('Recuperação Nativa ativada com sucesso!'),
      onError: () => toast.error('Não foi possível ativar a Recuperação Nativa'),
    })
  }

  const refresh = () => {
    void metrics.refetch()
    void carts.refetch()
  }

  const items = carts.data?.pages.flatMap((pageData) => pageData.items) ?? []

  return (
    <div className={page} data-testid="recupera-ai-screen">
      <div className={header}>
        <div className={headerText}>
          <h1 className={title}>Recupera AI</h1>
          <p className={subtitle}>
            Recuperação de carrinhos abandonados com inteligência artificial.
          </p>
        </div>
        {active ? (
          <SegmentedControl
            value={period}
            options={PERIOD_OPTIONS}
            onChange={(next) => onSearchChange({ ...search, period: next })}
            ariaLabel="Período"
          />
        ) : null}
      </div>

      {status.isPending ? (
        <Skeleton width="100%" height={120} />
      ) : status.isError ? (
        <ErrorState onRetry={() => void status.refetch()} />
      ) : !active ? (
        <RecuperaOptIn onActivate={activate} activating={optIn.isPending} />
      ) : (
        <>
          {metrics.isPending ? (
            <Skeleton width="100%" height={110} />
          ) : metrics.isError ? (
            <ErrorState onRetry={() => void metrics.refetch()} />
          ) : metrics.data !== undefined ? (
            <RecuperaMetricsCards metrics={metrics.data} />
          ) : null}

          <Card>
            <FilterToolbar>
              <SegmentedControl
                value={statusFilter ?? 'all'}
                options={STATUS_OPTIONS}
                onChange={(next) =>
                  onSearchChange({ ...search, status: next === 'all' ? undefined : next })
                }
                ariaLabel="Status"
              />
              <div className={filtersGroup}>
                <SelectField
                  aria-label="Produto"
                  value={search.product ?? ''}
                  options={productOptions}
                  onChange={(event) =>
                    onSearchChange({
                      ...search,
                      product: event.target.value === '' ? undefined : event.target.value,
                    })
                  }
                />
                <SelectField
                  aria-label="Ordenar"
                  value={sort}
                  options={[...SORT_OPTIONS]}
                  onChange={(event) =>
                    onSearchChange({ ...search, sort: event.target.value as RecuperaCartSort })
                  }
                />
                <div className={searchGrow}>
                  <SearchField
                    value={searchText}
                    onChange={setSearchText}
                    placeholder="Buscar por cliente ou produto..."
                    ariaLabel="Buscar carrinhos"
                  />
                </div>
                <Button
                  variant="secondary"
                  onClick={refresh}
                  leftIcon={<RotateCw size={16} aria-hidden />}
                  className={pushRight}
                  data-testid="recupera-ai-refresh-btn"
                >
                  Atualizar
                </Button>
              </div>
            </FilterToolbar>
            <RecuperaCartsTable
              items={items}
              isLoading={carts.isPending}
              isError={carts.isError}
              onRetry={() => void carts.refetch()}
              hasMore={carts.hasNextPage}
              isFetchingMore={carts.isFetchingNextPage}
              onLoadMore={() => void carts.fetchNextPage()}
            />
          </Card>
        </>
      )}
    </div>
  )
}
